<template>
  <details
    v-if="expand || expanded"
    class="banner banner-expandable flex-auto medicus-outline"
    :class="[type]"
    :open="expanded"
  >
    <summary class="banner-expandable__header">
      <m-icon v-if="icon" :name="icon" class="banner-expandable__icon" />
      <span v-if="label" class="banner-expandable__label">{{ label }}</span>
      <m-icon name="fa-solid fa-chevron-down" class="banner-expandable__chevron" />
    </summary>
    <div class="banner-expandable__content">
      <slot />
      <m-action v-if="actions" :model-value="actions" />
    </div>
  </details>
  <div v-else class="banner" :class="[type]">
    <m-icon v-if="icon" :name="icon"> </m-icon>
    <span v-if="iconDescription" class="sr-only">{{ iconDescription }}:</span>

    <div class="banner-content">
      <slot />
    </div>

    <m-action v-if="actions" class="banner-action" :model-value="actions" :ghost="false" small />
  </div>
</template>

<script setup lang="ts">
import MIcon from "../MIcon";
import { computed } from "vue";
import MAction from "../MAction/MAction.vue";

const props = defineProps({
  expand: Boolean,
  expanded: Boolean,

  label: {
    type: String,
    default: null,
  },

  type: {
    type: String,
    default: "system",
  },

  actions: Array,
  icon: {
    type: String,
    default: null,
  },
});

const icon = computed(() => {
  if (props.icon) {
    return props.icon;
  }

  switch (props.type) {
    case "info":
      return "fa-solid fa-info-circle";
    case "warning":
    case "danger":
      return "fa-solid fa-exclamation-triangle";
    case "success":
      return "fa-solid fa-check-circle";
    case "system":
      return "";
  }
  return "";
});

const iconDescription = computed(() => {
  switch (props.type) {
    case "info":
      return "Information";
    case "warning":
      return "Warning";
    case "danger":
      return "Danger";
    case "success":
      return "Success";
    case "system":
      return "";
  }

  // TODO add warning when icon description is not passed
  return "";
});
</script>

<style lang="scss">
div.banner:not(.banner-expandable) {
  padding: 10px 15px;
}

.banner.banner-expandable {
  align-items: normal;
  flex-direction: column;
  gap: 0;

  .banner-expandable__header {
    display: flex;
    flex-direction: row;
    align-items: center;
    box-sizing: border-box;
    min-height: 40px;
    padding: 8px 15px;
    // match the plain banner's icon→content gap (.banner { gap: 0.75rem })
    gap: 0.75rem;
    cursor: pointer;
    list-style: none;

    &::-webkit-details-marker {
      display: none;
    }
  }

  .banner-expandable__icon {
    font-size: 20px !important;
  }

  .banner-expandable__chevron {
    margin-left: auto;
    transition: transform 0.2s ease;
  }

  &[open] > .banner-expandable__header .banner-expandable__chevron {
    transform: rotate(180deg);
  }

  .banner-expandable__content {
    // no icon → content aligns with the title at the header gutter
    padding: 0 15px 1em;
  }

  // with an icon, indent content to align under the title (past 20px icon + 0.75rem gap)
  &:has(.banner-expandable__icon) .banner-expandable__content {
    padding-left: calc(35px + 0.75rem);
  }

  // icon colour per type — mirror the plain banner's `> .m-icon` colours
  &.system .banner-expandable__icon {
    color: var(--theme-blue);
  }

  &.info .banner-expandable__icon {
    color: var(--theme-blue);
  }

  &.warning .banner-expandable__icon {
    color: var(--yellow-dark-non-text);
  }

  &.danger .banner-expandable__icon {
    color: var(--status-red);
  }

  &.success .banner-expandable__icon {
    color: var(--status-green);
  }
}

.banner {
  position: relative;
  background: #fff;

  box-sizing: border-box;
  border-top-right-radius: 4px;
  border-bottom-right-radius: 4px;

  flex: 0 1 auto;

  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 0.75rem;
  &.has-title {
    padding: 13px 13px 15px 24px;
  }

  &::before {
    content: " ";
    position: absolute;
    height: auto;
    width: 5px;
    left: 0px;
    top: -1px;
    bottom: -1px;
  }

  .banner-content {
    flex: 1 1 auto;
  }

  .banner-action {
    height: 32px;
  }

  > .m-icon {
    font-size: 20px !important;
  }

  &.system {
    border: 1px solid var(--grey-lightest-non-text);
    &::before {
      background: var(--grey-lightest-non-text);
    }

    > .m-icon {
      color: var(--grey-lightest-non-text);
      color: var(--theme-blue);
    }
  }

  &.info {
    border: 1px solid var(--theme-blue);
    &::before {
      background: var(--theme-blue);
    }

    > .m-icon {
      color: var(--theme-blue);
    }
  }
  &.warning {
    border: 1px solid var(--yellow-dark-non-text);
    &::before {
      background: var(--yellow-dark-non-text);
    }

    > .m-icon {
      color: var(--yellow-dark-non-text);
    }
  }
  &.danger {
    border: 1px solid var(--status-red);
    &::before {
      background: var(--status-red);
    }

    > .m-icon {
      color: var(--status-red);
    }
  }
  &.success {
    border: 1px solid var(--status-green);
    &::before {
      background: var(--status-green);
    }

    > .m-icon {
      color: var(--status-green);
    }
  }
}
</style>
<style lang="scss">
.banner-expandable__label,
.banner-header {
  font-weight: bold;
  font-size: 14px;
  line-height: 150%;
}
</style>
